import { useEffect, useMemo } from 'react';
import { Outlet, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { liveSportsMenuQuery } from '@/api/queries';
import { useLoadLmtScript } from '@/hooks/useLoadLmtScript';
import { useLiveFeedSocket } from '@/live/useLiveFeedSocket';
import { toSlug } from '@/shell/sports-menu-utils';
import { HomeFilterMenuRail, LiveSportsMenuSlider, useSportsFavorites } from '@/ui';

/**
 * Live area layout: keeps the sports menu slider mounted while child routes swap
 * content below. Event details hides the slider for back + breadcrumb chrome.
 * LiveFeedProvider lives on the root route so the betslip rail shares the same feed.
 */
export const Route = createFileRoute('/live')({
  validateSearch: (search: Record<string, unknown>) => ({
    sport: typeof search.sport === 'string' ? search.sport : undefined,
    tournament: typeof search.tournament === 'string' ? search.tournament : undefined,
    media: search.media === 'stream' ? ('stream' as const) : undefined,
  }),
  component: LiveLayout,
});

function LiveLayout() {
  useLoadLmtScript();
  useLiveFeedSocket();
  const navigate = useNavigate();
  const { hasPrematchFavorites } = useSportsFavorites();
  const { sport: sportFromSearch } = Route.useSearch();
  const params = useParams({ strict: false });
  const eventIdFromRoute = typeof params.eventId === 'string' ? params.eventId : undefined;
  const { data: sports } = useQuery(liveSportsMenuQuery());

  const sortedSports = useMemo(
    () =>
      sports
        ? [...sports].filter((sport) => !sport.Blocked).sort((a, b) => a.Position - b.Position)
        : [],
    [sports],
  );

  const selectedSportSlug = useMemo(() => {
    if (sportFromSearch && sortedSports.some((sport) => toSlug(sport.Name) === sportFromSearch)) {
      return sportFromSearch;
    }
    if (sortedSports[0]) {
      return toSlug(sortedSports[0].Name);
    }
    return sportFromSearch ?? '';
  }, [sportFromSearch, sortedSports]);

  useEffect(() => {
    if (eventIdFromRoute || !sortedSports.length) return;

    const validSlug =
      sportFromSearch && sortedSports.some((sport) => toSlug(sport.Name) === sportFromSearch)
        ? sportFromSearch
        : toSlug(sortedSports[0]!.Name);

    if (sportFromSearch !== validSlug) {
      void navigate({
        to: '/live',
        search: { sport: validSlug, tournament: undefined, media: undefined },
        replace: true,
      });
    }
  }, [eventIdFromRoute, sportFromSearch, sortedSports, navigate]);

  const sportForPrematch = selectedSportSlug || 'football';

  return (
    <div className="flex flex-col gap-4">
      {!eventIdFromRoute && (
        <>
          <HomeFilterMenuRail
            context="live"
            value={null}
            showFavorites={hasPrematchFavorites}
            onValueChange={(id) => {
              if (id === 'prematch') {
                void navigate({
                  to: '/sports/$sport',
                  params: { sport: sportForPrematch },
                });
                return;
              }

              if (id === 'favorites') {
                void navigate({
                  to: '/sports/$sport/favorites',
                  params: { sport: sportForPrematch },
                });
                return;
              }

              if (id === 'last-minute') {
                void navigate({
                  to: '/sports/$sport/lastminute',
                  params: { sport: sportForPrematch },
                });
                return;
              }

              if (id === 'top-events') {
                void navigate({
                  to: '/sports/$sport/topevents',
                  params: { sport: sportForPrematch },
                });
                return;
              }

              if (id === 'today') {
                void navigate({
                  to: '/sports/$sport/today',
                  params: { sport: sportForPrematch },
                });
              }
            }}
          />

          <div className="-mx-4 lg:mx-0">
            <LiveSportsMenuSlider
              selectedSportSlug={selectedSportSlug}
              onSportSelect={(slug) => {
                void navigate({
                  to: '/live',
                  search: { sport: slug, tournament: undefined, media: undefined },
                });
              }}
            />
          </div>
        </>
      )}

      <Outlet />
    </div>
  );
}
